import React from 'react';
import { WarningIcon } from '../../../assets/icons/warning-icon.tsx';
import { RadioList, RadioListItem } from '../../../components/ui/button-radio-list.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmEventEditScope } from '../../../server/queries/utils/crm-event-build-input.ts';
import type { RouterOutputs } from '../../../utils/api.ts';
import { BaseConfirmationDialog } from '../../dialog/confirmation-dialog.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import type { ModalInnerProps } from '../../modal/index.tsx';
import { useDeleteEvent } from '../hooks/use-delete-event.ts';
export type DeleteEventModalProps = {
    onDeleted?: () => void;
    date: string;
    event: RouterOutputs['platoUser']['query']['events']['getEventById'];
} & ModalInnerProps;

export const DeleteEventModal = DialogStateManager.create<DeleteEventModalProps>(
    ({ onDeleted, event, date: occurrenceDate }: DeleteEventModalProps) => {
        const { t } = useLocale();
        const { toast } = useToast();
        const modal = useDialog();
        const [deleteScope, setDeleteScope] = React.useState<CrmEventEditScope>('single');

        const isRecurring = Boolean(event.recurrence);

        const handleDeleteSuccess = () => {
            toast({
                icon: 'success',
                title: t('events__delete_success', { ns: 'events' }),
            });
            onDeleted?.();
            modal.remove();
        };

        const handleDeleteError = (message?: string) => {
            toast({
                icon: 'warning',
                title: t('events__delete_error', { ns: 'events' }),
                description: message,
            });
        };

        const { mutate: deleteEvent, isPending: isDeleting } = useDeleteEvent(event, {
            onSuccess: handleDeleteSuccess,
            onError: handleDeleteError,
        });

        const handleDelete = () => {
            deleteEvent({ scope: deleteScope, occurrenceDate });
        };

        const handleCancel = () => {
            modal.remove();
        };

        return (
            <BaseConfirmationDialog
                focus="destroy"
                title={t('events__delete__title', { ns: 'events' })}
                description={t('events__delete_confirmation__body', {
                    ns: 'events',
                })}
                icon={WarningIcon}
            >
                {isRecurring && (
                    <div className="self-start">
                        <RadioList
                            value={deleteScope}
                            onValueChange={(val) => {
                                setDeleteScope(val as CrmEventEditScope);
                            }}
                        >
                            <RadioListItem
                                value="single"
                                label={t('events__recurrence_scope__single', { ns: 'events' })}
                            />
                            <RadioListItem
                                value="following"
                                label={t('events__recurrence_scope__following', { ns: 'events' })}
                            />
                        </RadioList>
                    </div>
                )}
                <BaseConfirmationDialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={handleCancel}>
                        {t('form__cancel')}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton onClick={handleDelete} isLoading={isDeleting}>
                        {t('events__delete', { ns: 'events' })}
                    </BaseConfirmationDialog.AcceptButton>
                </BaseConfirmationDialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
